/* ============================================================
   Subscription Picker — Custom element
   Renders a one-time vs subscribe picker on the PDP using
   Shopify's native selling_plan_groups data populated by Appstle.

   Reads the variant->allocations matrix from a sibling
   <script type="application/json" data-subscription-data> tag
   and reacts to:
     - <variant-selects> change events (variant switching)
     - purchase_type radio changes (one_time / subscribe)
     - selling plan <select> change

   Writes the chosen plan id to a hidden input named "selling_plan"
   inside the parent <form>, so that product-form.js picks it up
   when adding to cart.
   ============================================================ */
if (!customElements.get('subscription-picker')) {
  customElements.define(
    'subscription-picker',
    class SubscriptionPicker extends HTMLElement {
      connectedCallback() {
        const dataEl = this.querySelector('[data-subscription-data]');
        if (!dataEl) return;

        try {
          this._data = JSON.parse(dataEl.textContent);
        } catch (err) {
          console.error('[subscription-picker] Failed to parse data:', err);
          return;
        }

        this._defaultPurchaseType = this.dataset.defaultPurchaseType || 'subscribe';

        this._frames = {
          one_time: this.querySelector('[data-picker-frame][data-purchase-type="one_time"]'),
          subscribe: this.querySelector('[data-picker-frame][data-purchase-type="subscribe"]'),
        };
        if (!this._frames.one_time || !this._frames.subscribe) return;

        this._inputs = {
          one_time: this._frames.one_time.querySelector('input[type="radio"][name="purchase_type"]'),
          subscribe: this._frames.subscribe.querySelector('input[type="radio"][name="purchase_type"]'),
        };

        this._oneTimePriceEl = this.querySelector('[data-one-time-price]');
        this._subscribePriceEl = this.querySelector('[data-subscribe-price]');
        this._subscribeCompareEl = this.querySelector('[data-subscribe-compare-price]');
        this._badgeEl = this.querySelector('[data-subscribe-badge]');
        this._descriptionEl = this.querySelector('[data-subscription-description]');
        this._selectWrapperEl = this.querySelector('[data-subscription-plan-select-wrapper]');
        this._selectEl = this.querySelector('[data-subscription-plan-select]');
        this._collapseEl = this.querySelector('[data-subscribe-collapse]');
        this._disclosureEl = this.querySelector('[data-subscribe-disclosure]');
        this._disclosureTemplate = this.dataset.disclosureTemplate || '';

        const form = this.closest('form');
        this._variantIdInput = form ? form.querySelector('input[name="id"]') : null;
        this._sellingPlanInput = form ? form.querySelector('input[name="selling_plan"]') : null;

        // The native ".variant-total-price" line is redundant once we render
        // per-frame prices. Remove it so it cannot be re-shown by variant-picker.js
        // when switching to a variant without plans.
        const totalPriceContainer = document.querySelector('.variant-total-price');
        if (totalPriceContainer) totalPriceContainer.remove();

        this._bindEvents();
        this._applyVariantState(this._currentVariantId());
      }

      _bindEvents() {
        this._onDocumentChange = (e) => {
          if (e.target && e.target.closest('variant-selects')) {
            this._applyVariantState(this._currentVariantId());
          }
        };
        document.addEventListener('change', this._onDocumentChange);

        ['one_time', 'subscribe'].forEach((type) => {
          const input = this._inputs[type];
          if (input) input.addEventListener('change', () => this._onPurchaseTypeChange());
        });

        if (this._selectEl) {
          this._selectEl.addEventListener('change', () => this._onPlanChange());
        }
      }

      disconnectedCallback() {
        if (this._onDocumentChange) {
          document.removeEventListener('change', this._onDocumentChange);
        }
      }

      _currentVariantId() {
        if (!this._variantIdInput) return null;
        const value = Number(this._variantIdInput.value);
        return Number.isFinite(value) ? value : null;
      }

      _currentPurchaseType() {
        if (this._inputs.one_time && this._inputs.one_time.checked) return 'one_time';
        if (this._inputs.subscribe && this._inputs.subscribe.checked) return 'subscribe';
        return null;
      }

      _applyVariantState(variantId) {
        if (!variantId || !this._data || !this._data.variants) return;
        const variantData = this._data.variants[variantId];
        if (!variantData) return;

        this._currentVariant = variantData;

        const allocations = variantData.allocations || [];
        const hasPlans = allocations.length > 0;
        const subscriptionOnly = !!variantData.subscription_only;

        const oneTimeDisabled = hasPlans && subscriptionOnly;
        const subscribeDisabled = !hasPlans;

        this._setFrameDisabled('one_time', oneTimeDisabled);
        this._setFrameDisabled('subscribe', subscribeDisabled);

        if (this._oneTimePriceEl) {
          this._oneTimePriceEl.textContent = this._formatPrice(variantData.price);
        }

        this._populateSelect(allocations);

        let target = this._currentPurchaseType();
        if (target === 'one_time' && oneTimeDisabled) target = 'subscribe';
        if (target === 'subscribe' && subscribeDisabled) target = 'one_time';
        if (!target) {
          if (oneTimeDisabled) target = 'subscribe';
          else if (subscribeDisabled) target = 'one_time';
          else target = this._defaultPurchaseType;
        }

        if (this._inputs.one_time) this._inputs.one_time.checked = target === 'one_time';
        if (this._inputs.subscribe) this._inputs.subscribe.checked = target === 'subscribe';

        this._onPurchaseTypeChange();
      }

      _setFrameDisabled(type, disabled) {
        const frame = this._frames[type];
        const input = this._inputs[type];
        if (frame) frame.dataset.disabled = disabled ? 'true' : 'false';
        if (input) {
          input.disabled = disabled;
          if (disabled) input.checked = false;
        }
      }

      _populateSelect(allocations) {
        if (!this._selectEl) return;

        const previousValue = this._selectEl.value;
        this._selectEl.innerHTML = '';

        allocations.forEach((alloc) => {
          const option = document.createElement('option');
          option.value = String(alloc.selling_plan_id);
          option.textContent = alloc.name;
          this._selectEl.appendChild(option);
        });

        const stillExists = allocations.some((a) => String(a.selling_plan_id) === previousValue);
        if (stillExists) {
          this._selectEl.value = previousValue;
        } else if (allocations.length > 0) {
          this._selectEl.value = String(allocations[0].selling_plan_id);
        }
      }

      _onPurchaseTypeChange() {
        const type = this._currentPurchaseType();
        const isSubscribe = type === 'subscribe';

        if (this._collapseEl) this._collapseEl.dataset.open = isSubscribe ? 'true' : 'false';

        const hasPlans = !!(this._currentVariant && (this._currentVariant.allocations || []).length > 0);
        if (hasPlans) {
          this._updateSubscribeVisuals();
        } else {
          this._hideBadge();
        }

        if (!isSubscribe) {
          this._setSellingPlanInput(null);
        }
      }

      _hideBadge() {
        if (!this._badgeEl) return;
        this._badgeEl.classList.add('hidden');
        this._badgeEl.style.display = 'none';
        this._badgeEl.textContent = '';
      }

      _onPlanChange() {
        this._updateSubscribeVisuals();
      }

      _updateSubscribeVisuals() {
        if (!this._currentVariant || !this._currentVariant.allocations) return;
        if (!this._selectEl) return;

        const planId = this._selectEl.value;
        const alloc = this._currentVariant.allocations.find((a) => String(a.selling_plan_id) === String(planId));

        if (!alloc) return;

        if (this._subscribePriceEl) {
          this._subscribePriceEl.textContent = this._formatPrice(alloc.price);
        }

        if (this._subscribeCompareEl) {
          if (alloc.compare_at_price > alloc.price) {
            this._subscribeCompareEl.textContent = this._formatPrice(alloc.compare_at_price);
            this._subscribeCompareEl.classList.remove('hidden');
          } else {
            this._subscribeCompareEl.classList.add('hidden');
          }
        }

        if (this._badgeEl) {
          if (alloc.badge) {
            this._badgeEl.textContent = alloc.badge;
            this._badgeEl.classList.remove('hidden');
            this._badgeEl.style.display = 'inline-flex';
          } else {
            this._hideBadge();
          }
        }

        if (this._descriptionEl) {
          if (!!alloc?.description) {
            this._descriptionEl.style.display = 'block';
            this._descriptionEl.innerHTML = alloc.description || '';
          } else {
            this._descriptionEl.style.display = 'none';
          }
        }

        this._setSellingPlanInput(alloc.selling_plan_id);
        this._renderDisclosure(alloc);
      }

      _setSellingPlanInput(planId) {
        if (!this._sellingPlanInput) return;
        this._sellingPlanInput.value = planId == null || planId === '' ? '' : String(planId);
      }

      _formatFrequency(count, interval) {
        if (!interval) return '';
        const map = { DAY: 'day', WEEK: 'week', MONTH: 'month', YEAR: 'year' };
        const unit = map[interval] || interval.toLowerCase();
        const n = Number(count) || 1;
        if (n === 1) return unit;
        return `${n} ${unit}s`;
      }

      _parseFrequency(raw) {
        const m = String(raw || '').match(/^(\d+)(DAY|WEEK|MONTH|YEAR)/);
        if (!m) return null;
        return { count: Number(m[1]), interval: m[2] };
      }

      _renderDisclosure(alloc) {
        if (!this._disclosureEl || !this._disclosureTemplate) return;
        if (!alloc) {
          this._disclosureEl.textContent = '';
          return;
        }
        const parsed = this._parseFrequency(alloc.frequency_value);
        if (!parsed) {
          this._disclosureEl.textContent = '';
          return;
        }
        const price = this._formatPrice(alloc.price);
        const frequency = this._formatFrequency(parsed.count, parsed.interval);
        this._disclosureEl.textContent = this._disclosureTemplate
          .replace('[price]', price)
          .replace('[frequency]', frequency);
      }

      _formatPrice(cents) {
        if (window.Robin && typeof window.Robin.getPrice === 'function') {
          return window.Robin.getPrice((cents || 0) / 100);
        }
        return '$' + ((cents || 0) / 100).toFixed(2);
      }
    },
  );
}
